import {endAt, equalTo, get, limitToFirst, orderByChild, QueryConstraint, startAt} from "@firebase/database";
import {SvgIcon} from "@mui/material";
import {BaseApp, Context, ContextTrait, ContextType} from "./BaseApp";
import {Member, Members, User} from "./entities";
import {dbRef, GLOBAL_PATHS, ProvisioningContext, Rel, Rule, SystemKeys} from "./database";
import {deleteObject, getStorage, ref,} from "firebase/storage";
import {JsonObject} from "./json/json-object";
import {JsonProperty} from "./json/json-property";
import {findIcon} from "./icons";
import {createGuestMemberAuth, getMemberAuth, MemberAuth} from "./auth";
import {JSON_OBJECT, Type} from "./json/helpers";
import {ReactElement, ReactNode} from "react";
import {PluginApp} from "./PluginApp";
import {ProvisionedApp} from "./ProvisionedApp";
import {FormGen, formGenFileStoreGet} from "./formgen";
import {MimeTypeMap} from "./mime_types";
import {md5, md5_uuid} from "./md5";
import {merge} from "./lodash.merge";
import {PathProps} from "./index";
import defaultUserImage from "./res/images/default_user.png";
import {sleep} from "./sleep";
import {StringUtil} from "./string_util";
import {TableDataContainer} from "./TableDataContainer";
import {Environment} from "../common/types";

export const ENV: Environment = Environment.PROD;

// Display string - shows the string (if defined) or '-'
export function $DS(str: string, default_?: string): string {
  return str ?? (default_ || "-");
}

export enum Orientation {
  LANDSCAPE = "landscape",
  PORTRAIT = "portrait",
}

export type Region = {
  country: string,
  locale?: string,
}

export interface FilesBrowserFactory {
  render(path: PathProps): ReactElement;
}

export interface AppPrefListener {
  onPrefChanged(key: string): void;
}

export abstract class BaseKeyText<T> {

  constructor(readonly key: T, readonly text: string, readonly description?: string) {
  }
}

export class KeyText extends BaseKeyText<any> {

  constructor(key: any, text: string, description?: string) {
    super(key, text, description);
  }
}

export class KeyTextNumber extends BaseKeyText<Number> {

  constructor(key: number, text: string, description?: string) {
    super(key, text, description);
  }
}

export class KeyTextString extends BaseKeyText<string> {

  constructor(key: string, text: string, description?: string) {
    super(key, text, description);
  }
}

export function $KTS(key: string, text: string, description?: string) {
  return new KeyTextString(key, text, description);
}

export class KeyTextStrings {

  static fromList(list: string[]): KeyTextStrings {
    return new KeyTextStrings(list.map(item => $KTS(StringUtil.toSlug(item), item)));
  }

  constructor(readonly values: KeyTextString[]) {
  }

  asArray(): string[][] {
    return this.values.map(value => [value.key, value.text]);
  }

  findText(key: string): string | undefined {
    return this.values.find(kt => kt.key === key)?.text;
  }
}

export class KeyMetadata<T> {

  constructor(readonly key: string, readonly metadata: T) {
  }
}

export function $KTM<T>(key: string, metadata: T): KeyMetadata<T> {
  return new KeyMetadata<T>(key, metadata);
}

export class KeyMetadatas<T> {

  constructor(readonly values: KeyMetadata<T>[]) {
  }

  findMetadata(key: string): T | undefined {
    return this.values.find(kt => kt.key === key)?.metadata;
  }

  private readonly __ktsMap = new Map<string, KeyTextStrings>();

  kts(textPropertyName?: string): KeyTextStrings {
    const key = textPropertyName || "text";
    let kts = this.__ktsMap.get(key);
    if (!kts) {
      kts = new KeyTextStrings(this.values.map(value => $KTS(value.key, value.metadata[key])));
      this.__ktsMap.set(key, kts);
    }
    return kts;
  }

  private __metadataValuesMap: Map<string, KeyMetadatas<any>>;

  metadataValues<T>(valuePropertyName: string): KeyMetadatas<T> {
    if (!this.__metadataValuesMap) {
      this.__metadataValuesMap = new Map<string, any>();
    }
    let values = this.__metadataValuesMap.get(valuePropertyName);
    if (!values) {
      values = new KeyMetadatas<T>(this.values.map(value => new KeyMetadata(value.key, value.metadata[valuePropertyName])));
      this.__metadataValuesMap.set(valuePropertyName, values);
    }
    return values as KeyMetadatas<T>;
  }
}

export enum DaysOfWeek {
  SUNDAY = "sun",
  MONDAY = "mon",
  TUESDAY = "tue",
  WEDNESDAY = "wed",
  THURSDAY = "thu",
  FRIDAY = "fri",
  SATURDAY = "sat",
}

export const DAYS_OF_WEEK_KTS = new KeyTextStrings([
  $KTS(DaysOfWeek.SUNDAY, "Sunday"),
  $KTS(DaysOfWeek.MONDAY, "Monday"),
  $KTS(DaysOfWeek.TUESDAY, "Tuesday"),
  $KTS(DaysOfWeek.WEDNESDAY, "Wednesday"),
  $KTS(DaysOfWeek.THURSDAY, "Thursday"),
  $KTS(DaysOfWeek.FRIDAY, "Friday"),
  $KTS(DaysOfWeek.SATURDAY, "Saturday"),
]);

export type FileUploadOptions = {
  defer?: boolean,
}

export class ColorValue {

  constructor(readonly color?: string, readonly background?: string) {
  }
}

export class CurrencyType {

  static readonly DOLLAR = new CurrencyType("$");
  static readonly RUPEE = new CurrencyType("₹");

  private constructor(readonly prefix: string) {
  }

  getValue(n: number): number {
    return ((n || 0) / 100);
  }

  getText(n: number): string {
    return `${this.prefix} ${((n || 0) / 100).toFixed(2)}`;
  }
}

export function CurrencyPrefix(override?: CurrencyType): string {
  return (override || BaseApp.CONTEXT.getAppConfig().styleConfig?.currencyType || CurrencyType.DOLLAR).prefix;
}

export function CurrencyText(n: number, override?: CurrencyType): string {
  return (override || BaseApp.CONTEXT.getAppConfig().styleConfig?.currencyType || CurrencyType.DOLLAR).getText(n);
}

export enum DateRangeType {
  TODAY = "today",
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
  YEAR_TO_DATE = "year_to_date",
  YESTERDAY = "yesterday",
  LAST_WEEK = "last_week",
  LAST_MONTH = "last_month",
  LAST_YEAR = "last_year",
  CUSTOM = "custom",
}

export const DATE_RANGE_TYPE_KTS = new KeyTextStrings([
  $KTS(DateRangeType.TODAY, "Today"),
  $KTS(DateRangeType.YESTERDAY, "Yesterday"),
  $KTS(DateRangeType.WEEK, "This Week"),
  $KTS(DateRangeType.MONTH, "This Month"),
  $KTS(DateRangeType.YEAR, "This Year"),
  $KTS(DateRangeType.YEAR_TO_DATE, "Year to Date"),
  $KTS(DateRangeType.LAST_WEEK, "Last Week"),
  $KTS(DateRangeType.LAST_MONTH, "Last Month"),
  $KTS(DateRangeType.LAST_YEAR, "Last Year"),
  $KTS(DateRangeType.CUSTOM, "Custom"),
]);

export class UnitStringVariants {

  constructor(readonly kts: KeyTextString[]) {
  }
}

export abstract class UnitStringFactory<T extends UnitString> {

  constructor(readonly name: string, readonly variants?: UnitStringVariants) {
  }

  abstract create(variantKey: string, value: string[]): T;
}

export abstract class UnitString {

  private static readonly __TYPES__ = new Map<string, UnitStringFactory<any>>();

  static register(factory: UnitStringFactory<any>): void {
    this.__TYPES__.set(factory.name, factory);
  }

  private _str: string[] = [];
  private variantKey?: string;

  constructor(readonly name: string) {
  }

  getValue(): string[] {
    return this._str;
  }

  setValue(_str: string[]) {
    this._str = _str;
  }

  getVariantKey(): string {
    return this.variantKey;
  }

  setVariantKey(key: string): void {
    this.variantKey = key;
  }

  toString(): string {
    return `@@${this.name}\t${this.variantKey}\t${this._str}`;
  }

  static fromString<T extends UnitString>(str: string): T | undefined | null {
    if (!str?.startsWith("@@")) {
      return null;
    }
    const tok = str.split("\t");
    return UnitString.__TYPES__.get(tok[0])?.create(tok[1], JSON.parse(tok[2]) as string[])
  }
}

export class HeightUnitString extends UnitString {

  static readonly NAME = "height";

  constructor() {
    super(HeightUnitString.NAME);
  }
}

UnitString.register(new class extends UnitStringFactory<HeightUnitString> {
  constructor() {
    super(HeightUnitString.NAME, new UnitStringVariants([
      $KTS("ft-in", "Feet/inches"),
      $KTS("ft", "Feet (dec.)"),
      $KTS("m-cm", "Meters/cms"),
      $KTS("m-cm", "Meters (dec.)"),
    ]));
  }

  create(variantKey: string, value: string[]): any {
    const string = new HeightUnitString();
    string.setValue(value);
    string.setVariantKey(variantKey);
    return string;
  }
});

export class WeightUnitString extends UnitString {

  static readonly NAME = "weight";

  constructor() {
    super(WeightUnitString.NAME);
  }
}

UnitString.register(new class extends UnitStringFactory<WeightUnitString> {
  constructor() {
    super(WeightUnitString.NAME, new UnitStringVariants([
      $KTS("kgs", "Kgs"),
      $KTS("lbs", "Lbs"),
      $KTS("lbs-oz", "Lbs/ounces"),
    ]));
  }

  create(variantKey: string, value: string[]): any {
    const string = new WeightUnitString();
    string.setValue(value);
    string.setVariantKey(variantKey);
    return string;
  }
});

export enum ChangeType {
  ADDED,
  UPDATED,
  DELETED,
}

export interface Observer<T = any> {
  onChanged(item: T, changeType?: ChangeType);
}

export interface IObservable<OBSERVER> {
  registerObserver(observer: OBSERVER);

  unregisterObserver(observer: OBSERVER);
}

export interface IObservable1<OBSERVER> {
  registerObserver1(id: string, observer: OBSERVER);

  unregisterObserver1(id: string, observer: OBSERVER);
}

export abstract class Observable<OBSERVER> implements IObservable<OBSERVER> {

  protected observers: OBSERVER[] = [];

  registerObserver(observer: OBSERVER) {
    const wasEmpty = this.observers.length === 0;
    if (!this.observers.includes(observer)) {
      this.observers.push(observer);
    }
    if (wasEmpty) {
      this.onStartObserving();
    }
  }

  protected onStartObserving() {
  }

  unregisterObserver(observer: OBSERVER) {
    this.observers = this.observers.filter(value => value !== observer);
    if (this.observers.length === 0) {
      this.onStopObserving();
    }
  }

  protected onStopObserving() {
  }
}

export class DefaultObservable<T> extends Observable<T> {

  constructor(private readonly startObserving?: () => void, private readonly stopObserving?: () => void) {
    super();
  }

  protected onStartObserving() {
    this.startObserving?.();
  }

  protected onStopObserving() {
    this.stopObserving?.();
  }
}

export class SimpleObservable<T> extends DefaultObservable<Observer<T>> {

  notify(item: T): void {
    this.observers.forEach(observer => observer.onChanged(item));
  }
}

export abstract class Observable1<T> implements IObservable1<T> {

  protected readonly observersMap = new Map<string, T[]>();

  registerObserver1(id: string, observer: T) {
    let observers = this.getObservers(id);
    const wasEmpty = observers.length === 0;
    if (!observers.includes(observer)) {
      observers.push(observer);
    }
    if (wasEmpty) {
      this.onStartObserving(id);
    }
  }

  getObservers(id: string) {
    let observers = this.observersMap.get(id);
    if (!observers) {
      observers = [];
      this.observersMap.set(id, observers);
    }
    return observers;
  }

  protected onStartObserving(id: string) {
  }

  unregisterObserver1(id: string, observer: T) {
    let observers = this.observersMap.get(id);
    if (!observers) {
      return;
    }
    observers = observers.filter(value => value !== observer);
    this.observersMap.set(id, observers);
    if (observers.length === 0) {
      this.onStopObserving(id);
    }
  }

  protected onStopObserving(id: string) {
  }
}

export class DefaultObservable1<T> extends Observable1<T> {

  constructor(private readonly startObserving?: (id: string) => void, private readonly stopObserving?: (id: string) => void) {
    super();
  }

  protected onStartObserving(id: string) {
    this.startObserving?.(id);
  }

  protected onStopObserving(id: string) {
    this.stopObserving?.(id);
  }
}

export class SimpleObservable1<T> extends DefaultObservable1<Observer<T>> {

  notify(id: string, item: T): void {
    (this.observersMap.get(id))?.forEach(observer => observer.onChanged(item));
  }

  notifyAll<V>(item: V, transform: Callable1<string, V, T>): void {
    for (const key of this.observersMap.keys()) {
      this.notify(key, transform(key, item));
    }
  }
}

type BaseConfig = {
  shared?: boolean, // Not stored per member if true.
  scopeRoot?: boolean, // Stored in root context (and NOT in app/<provisionedAppId> for example, even for provisioned apps). This is useful for data types like directory users etc., which are shared across many apps.
  overrideProvisioningContext?: ProvisioningContext,
}

@JsonObject()
export abstract class BaseObject {

  @JsonProperty()
  readonly id: string = "";

  member: Member;

  @JsonProperty()
  creator: string;

  @JsonProperty()
  created: number;

  protected constructor(creator?: string, created?: number) {
    this.creator = creator;
    this.created = created;
  }

  protected getRules(): Rule[] {
    return [];
  }

  // Return associated files that are deleted when this object is deleted.
  getFiles(): string[] {
    return BaseObject.getFilesOf(this);
  }

  static getFilesOf(obj: any): string[] {
    if (!obj) {
      return [];
    }
    return formGenFileStoreGet(Object.getPrototypeOf(obj))?.getFiles(obj) || [];
  }

  async onAfterObjectDeserialized(): Promise<void> {
  }

  async onBeforeObjectSerialized(): Promise<void> {
  }

  async onBeforeObjectDeleted(): Promise<void> {
    await Promise.all(this.getFiles()?.map(fileUrl => {
      if (!fileUrl) {
        return null;
      }
      return deleteObject(ref(getStorage(), fileUrl));
    }) || []);
  }

  clone<T extends BaseObject>(dataType: Type<T>): T {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), dataType);
  }
}

@JsonObject()
export abstract class TypedObject extends BaseObject {

  private loaded: boolean = false;

  protected basePath(): string | null {
    return null;
  }

  protected static async _load<T extends TypedObject>(creator: () => T): Promise<T> {
    const obj = creator();
    await obj.load();
    return obj;
  }

  protected static async _loadWithProvisioningContext<T extends TypedObject>(creator: () => T, provisioningContext: ProvisioningContext): Promise<T> {
    const obj = creator();
    await obj.load(null, provisioningContext);
    return obj;
  }

  protected objectConfig(): ObjectConfig | null {
    return null;
  }

  createDefaultLoader(): DefaultObjectLoader<any> {
    return new DefaultObjectLoader<any>(this.basePath(), this.id, this.getType(), this.objectConfig());
  }

  private defaultLoader: DefaultObjectLoader<any>;

  getDefaultLoader(): DefaultObjectLoader<any> {
    if (!this.defaultLoader) {
      this.defaultLoader = this.createDefaultLoader();
    }
    return this.defaultLoader;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  async loadReturn(forceReload?: boolean, overrideProvisioningContext?: ProvisioningContext): Promise<this> {
    await this.load(forceReload, overrideProvisioningContext);
    return this;
  }

  async load(forceReload?: boolean, overrideProvisioningContext?: ProvisioningContext): Promise<void> {
    if (this.loaded && !forceReload) {
      return;
    }
    const loader = this.getDefaultLoader();
    loader.setOverrideProvisioningContext(overrideProvisioningContext);
    const object = await loader.loadObject();
    if (object) {
      merge(this, object);
      this.loaded = true;
    }
    await this.onAfterObjectDeserialized();
  }

  async reload(): Promise<void> {
    await this.load(true);
  }

  async save(): Promise<void> {
    await this.getDefaultLoader().setObject(this);
  }

  async delete(): Promise<void> {
    await this.getDefaultLoader().deleteObject(this);
  }

  protected abstract getType(): Type<any>;
}

@JsonObject()
export class ChecklistItem {

  @JsonProperty()
  readonly key: string;

  @JsonProperty()
  readonly text: string;

  constructor(key: string, text: string) {
    this.key = key;
    this.text = text;
  }
}

@JsonObject()
export class ChecklistObject extends TypedObject {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly items: ChecklistItem[] = []

  constructor(id: string, readonly config: ObjectConfig) {
    super(null, null);
    this.id = id;
  }

  protected basePath(): string | null {
    return "checklist";
  }

  createDefaultLoader(): DefaultObjectLoader<ChecklistObject> {
    return new DefaultObjectLoader<ChecklistObject>(this.basePath(), this.id, this.getType(), this.config);
  }

  protected getType(): Type<any> {
    return ChecklistObject;
  }
}

export abstract class AbstractObjectLoader<T extends BaseObject> extends Observable<OnObjectListener<T>> {

  protected abstract objectPath(memberScope: string): string;

  protected abstract deserializeObject(value: any): T;

  protected abstract serializeObject(object: T): any;

  abstract loadObject(): Promise<T | null>;

  abstract getOrLoadObject(): Promise<T | null>;

  abstract getObject(): T | undefined;

  abstract setObject(object: T, rel?: Rel): Promise<void>;

  abstract deleteObject(object: T): Promise<void>;
}


export enum ObjectChange {
  CHANGED,
}

export interface OnObjectListener<T extends BaseObject> {

  onObjectChanged(object: T, change: ObjectChange);
}

export type ObjectConfig = BaseConfig & {}

export abstract class BaseObjectLoader<T extends BaseObject> extends AbstractObjectLoader<T> {

  private readonly memberAuth = getMemberAuth();

  protected object: T;

  private objectLoaded: boolean = false;

  private overrideProvisioningContext?: ProvisioningContext;

  constructor(private readonly basePath: string | null, private readonly id: string, private readonly config?: ObjectConfig) {
    super();
  }

  setOverrideProvisioningContext(overrideProvisioningContext: ProvisioningContext) {
    this.overrideProvisioningContext = overrideProvisioningContext;
  }

  private getProvisioningContext(): ProvisioningContext {
    return this.overrideProvisioningContext || this.config?.overrideProvisioningContext || ProvisioningContext.DEFAULT;
  }

  protected objectPath(memberScope: string): string {
    return (this.basePath || "") + memberScope + (this.id ? "/" + this.id : "");
  }

  protected abstract deserializeObject(value: any): T;

  protected async onAfterObjectDeserialized(object: T): Promise<T> {
    await object.onAfterObjectDeserialized();
    return object;
  }

  protected async onBeforeObjectSerialized(object: T): Promise<T> {
    await object.onBeforeObjectSerialized();
    return object;
  }

  protected abstract serializeObject(object: T): any;

  private listPath(): string {
    let contextScope: string = "";
    let provisionedAppId: string;
    if (BaseApp.CONTEXT.contextType() === ContextType.PLUGIN) {
      contextScope = "plugins/" + (BaseApp.CONTEXT as PluginApp).getPluginId() + "/";
    } else if (!this.config?.scopeRoot && BaseApp.CONTEXT.contextType() === ContextType.PROVISIONED_APP && Boolean(provisionedAppId = (BaseApp.CONTEXT as ProvisionedApp).getProvisionedAppId())) {
      contextScope = "apps/" + provisionedAppId + "/";
    }
    const memberScope = this.config?.shared ? "" : ("/" + this.memberAuth.member.memberId);
    return contextScope + this.objectPath(memberScope);
  }

  protected onStartObserving() {
    const path = this.listPath();
    this.getProvisioningContext().dbRef_onVal(path, (result) => {
      const value = result.val();
      if (value) {
        this.onObjectValueChanged(value, ObjectChange.CHANGED);
      }
    });
  }

  private onObjectValueChanged(value: any | string, change: ObjectChange) {
    const item = this.deserializeObject(value);
    if (!item) {
      return;
    }
    this.observers.forEach(observer => observer.onObjectChanged(item, change));
  }

  async loadObject(): Promise<T | null> {
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath());
    if (val) {
      return await this.loadObjectInternal(val);
    }
    return Promise.resolve(null);
  }

  private async loadObjectInternal(val) {
    let object = this.deserializeObject(val);
    if (object.creator) {
      object.member = await Members.getInstance().getOrLoadMember(object.creator);
    }
    object = await this.onAfterObjectDeserialized(object);
    this.objectLoaded = true;
    return object;
  }

  async getOrLoadObject(): Promise<T | null> {
    if (!this.objectLoaded) {
      this.object = await this.loadObject();
    }
    return Promise.resolve(this.getObject());
  }

  getObject(): T | null {
    return this.object;
  }

  async setObject(obj: T, rel?: Rel): Promise<void> {
    // BaseApp.CONTEXT.notifyAppEvent("cloud_status", CloudStatus.SAVING);
    obj = await this.onBeforeObjectSerialized(obj);
    const item = this.serializeObject(obj);
    await this.getProvisioningContext().dbRef_setVal(this.listPath(), item, rel);
    // BaseApp.CONTEXT.notifyAppEvent("cloud_status", CloudStatus.SAVED);
  }

  protected async onBeforeObjectDeleted(object: T): Promise<void> {
    await object.onBeforeObjectDeleted();
  }

  async deleteObject(object: T): Promise<void> {
    await this.onBeforeObjectDeleted(object);
    await this.getProvisioningContext().dbRef_removeVal(this.listPath());
  }
}

export class DefaultObjectLoader<T extends BaseObject> extends BaseObjectLoader<T> {

  constructor(basePath: string, id: string, private readonly type: Type<T>, config?: ObjectConfig) {
    super(basePath, id, config);
  }

  protected deserializeObject(value: any): T {
    return JSON_OBJECT.deserializeObject(value, this.type);
  }

  protected serializeObject(object: T): any {
    return JSON_OBJECT.serializeObject(object);
  }
}

export abstract class AbstractListItem {

  @JsonProperty()
  id: string;

  constructor(id: string) {
    this.id = id;
  }
}

export abstract class AbstractListItemsLoader<T extends AbstractListItem | string> extends Observable<OnListItemsListener<T>> {

  constructor(protected readonly config?: ListConfig<T>) {
    super();
    this.overrideProvisioningContext = config?.overrideProvisioningContext;
  }

  protected overrideProvisioningContext?: ProvisioningContext;

  setOverrideProvisioningContext(overrideProvisioningContext: ProvisioningContext) {
    this.overrideProvisioningContext = overrideProvisioningContext;
  }

  protected getProvisioningContext(): ProvisioningContext {
    return this.overrideProvisioningContext || ProvisioningContext.DEFAULT;
  }

  protected getRules(): Rule[] {
    return [];
  }

  protected formatTemplate(template: string, keyTexts: KeyTextString[]): string {
    const pathComponents = template.split(/\/+/);
    pathComponents.forEach((component, index) => {
      if (component.startsWith("$")) {
        pathComponents[index] = keyTexts.find(kts => kts.key === component.substring(1)).text;
      }
    })
    return pathComponents.join("/");
  }

  protected basePathTemplate(): string | null | undefined {
    return undefined;
  }

  protected basePathFromTemplate(keyTexts: KeyTextString[]): string {
    let template = this.basePathTemplate();
    if (!template) {
      throw new Error("basePathTemplate not defined.");
    }
    return this.formatTemplate(template, keyTexts);
  }

  protected abstract basePath(): string;

  protected abstract deserializeItem(value: any): T;

  protected abstract serializeItem(item: T): any;

  protected abstract sortOrder(item1: T, item2: T): number;

  abstract loadListItems(options?: LoadListOptions): Promise<void>;

  abstract getOrLoadListItems(options?: LoadListOptions): Promise<T[]>;

  abstract getListItems(): T[];

  abstract getListItem(id: string): T | undefined;

  abstract loadListItem(id: string): Promise<T | null>;

  abstract getOrLoadItem(id: string): Promise<T | null>;

  abstract addListItem(item: T, rel?: Rel): Promise<void>;

  abstract deleteAllListItems(): Promise<void>;

  abstract deleteListItem(item: T): Promise<void>;

  abstract deleteListItemById(itemId: string): Promise<void>;

  getDemoItems(options?: LoadListOptions): T[] {
    return [];
  }
}

export abstract class AbstractListItemsLoaderBase<T extends AbstractListItem> extends AbstractListItemsLoader<T> {

  protected readonly items = new Map<string, T>(); // id -> item
  private sortedItems: T[];
  protected itemsLoaded: boolean = false;

  async maybeLoadListItems(): Promise<void> {
    if (this.itemsLoaded) {
      return;
    }
    await this.loadListItems();
  }

  async getOrLoadListItems(options?: LoadListOptions): Promise<T[]> {
    // Don't think we need sortedItems check here.
    // if (!this.sortedItems) {
    if (!this.itemsLoaded) {
      await this.loadListItems();
    }
    return this.getListItems();
  }

  getListItems(): T[] {
    if (!this.itemsLoaded) {
      console.error("Getting items but they're not loaded yet.");
    }
    if (!this.sortedItems) {
      this.sortedItems = [...this.items.values()].sort(this.sortOrder.bind(this));
    }
    return this.sortedItems;
  }

  getListItemsCount(): number {
    return this.getListItems()?.length || 0;
  }

  getListItem(id: string): T | undefined {
    if (!id) {
      return undefined;
    }
    // Don't use getListItems() here as that will instantiate sortedItems which we want only when loading entire list.
    return [...this.items.values()].find(value => value.id === id);
  }

  async getOrLoadItem(id: string): Promise<T | null> {
    if (!id) {
      return null;
    }
    const item = this.getListItem(id);
    if (item) {
      return item;
    }
    return this.loadListItem(id);
  }

  protected sortOrder(item1: T, item2: T): number {
    return 0;
  }
}

export enum ListItemChange {
  ADDED,
  CHANGED,
  REMOVED,
}

export interface OnListItemsListener<T extends AbstractListItem | string> {

  __treeItemsListener?: OnTreeItemsListener<any>;

  onItemChanged(item: T, change: ListItemChange): void;
}

// Convenience wrapper for use with FilterListItemsLoader<T>
export class OnFilterListItemsListener<T extends AbstractListItem | string> implements OnListItemsListener<T> {

  constructor(readonly observer: OnListItemsListener<T>, private readonly filter: Filter<T>) {
  }

  onItemChanged(item: T, change: ListItemChange) {
    if (this.filter(item)) {
      this.observer.onItemChanged(item, change);
    }
  }
}

export type UpdateItemRels<T> = Callable<T, Rel[]>;
export type LoadItemsRel = Callable<void, Rel>;
export type ListConfig<T> = BaseConfig & {
  // Relations that are automatically added/ deleted when addListItem/ deleteListItem is called.
  updateItemRels?: UpdateItemRels<T>,
  // If this is set, the loader will selectively load only the items that are children of this rel's path.
  // Usually update and load rels should be set together. See messaging/chat/ChatThreads for an example.
  loadItemsRel?: LoadItemsRel,
}

@JsonObject()
export abstract class BaseListItem extends AbstractListItem {

  member: Member;

  @JsonProperty()
  creator: string;

  @JsonProperty()
  created: number;

  @JsonProperty()
  createdBy: string;

  @JsonProperty()
  createdAt: number;

  @JsonProperty()
  updatedBy: string;

  @JsonProperty()
  updatedAt: number;

  @JsonProperty()
  deletedBy: string;

  @JsonProperty()
  deletedAt: number;

  protected constructor(id: string, creator?: string, created?: number) {
    super(id);
    this.creator = this.createdBy = creator;
    this.created = this.createdAt = created;
  }

  // Return associated files that are deleted when this item is deleted.
  getFiles(): string[] {
    return BaseListItem.getFilesOf(this);
  }

  static getFilesOf(obj: any): string[] {
    if (!obj) {
      return [];
    }
    return formGenFileStoreGet(Object.getPrototypeOf(obj))?.getFiles(obj) || [];
  }

  getRels(): Rel[] {
    return [];
  }

  // Private. Do not use outside this file.
  _afterItemDeserialized(val: any): Promise<void> {
    this.creator = this.createdBy = this.createdBy || val?.["creator"];
    this.created = this.createdAt = this.createdAt || val?.["created"];
    return this.onAfterItemDeserialized();
  }

  async onAfterItemDeserialized(): Promise<void> {
  }

  _beforeItemSerialized(): Promise<void> {
    return this.onBeforeItemSerialized();
  }

  async onBeforeItemSerialized(): Promise<void> {
  }

  async onBeforeItemDeleted(): Promise<void> {
    await Promise.all(this.getFiles()?.map(fileUrl => {
      if (!fileUrl) {
        return null;
      }
      return null;
      // return deleteObject(ref(getStorage(), fileUrl));
    })?.filter(Boolean) || []);
  }

  clone<T extends BaseListItem>(dataType: Type<T>): T {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), dataType);
  }

  static readonly CREATED_BY_REL_NAME = "createdBy";

  // This rel is useful when we this object is shared (and therefore NOT under the creator's memberId) and we want to lookup by creator.
  createdByRel() {
    return new Rel(Member.USERS_REL_NAME, this.createdBy, this.id, false, true);
  }
}

function constructChildrenPath(basePath: string, config: BaseConfig, memberAuth: MemberAuth, relName: string, id: string): string {
  let contextScope: string = "";
  if (BaseApp.CONTEXT.contextType() === ContextType.PLUGIN) {
    contextScope = "plugins/" + (BaseApp.CONTEXT as PluginApp).getPluginId() + "/";
  } else if (!config?.scopeRoot && BaseApp.CONTEXT.contextType() === ContextType.PROVISIONED_APP) {
    contextScope = "apps/" + (BaseApp.CONTEXT as ProvisionedApp).getProvisionedAppId() + "/";
  }
  const memberScope = config?.shared ? "" : ("/" + memberAuth.member.memberId);
  return contextScope + basePath + memberScope + "/" + SystemKeys.REL + "/" + relName + "/" + id + "/";
}

function constructListPath(basePath: string, config: BaseConfig, memberAuth: MemberAuth): string {
  if (!(basePath = basePath?.trim())) {
    basePath = "";
  }
  if (!basePath.startsWith("/")) {
    basePath = "/" + basePath;
  }
  if (basePath.startsWith("/__global__/") || GLOBAL_PATHS.includes(basePath)) {
    return basePath;
  }
  let contextScope: string = "";
  if (BaseApp.CONTEXT.contextType() === ContextType.PLUGIN) {
    contextScope = "plugins/" + (BaseApp.CONTEXT as PluginApp).getPluginId() + "/";
  } else if (!config?.scopeRoot && BaseApp.CONTEXT.contextType() === ContextType.PROVISIONED_APP) {
    const appId = (BaseApp.CONTEXT as ProvisionedApp).getProvisionedAppId();
    contextScope = appId ? "apps/" + appId + "/" : "";
  }
  const memberScope = config?.shared ? "" : ("/" + memberAuth.member.memberId);
  return contextScope + basePath + memberScope;
}

export type LoadListOptions = {
  limitCount?: number,
  orderByChild?: {
    key: string,
    startAt?: number | string | boolean | null,
    endAt?: number | string | boolean | null,
    equalTo?: number | string | boolean | null,
  },
}

export type TreeConfig = BaseConfig & {}

export abstract class ListKeysLoader extends AbstractListItemsLoader<string> {

  private readonly memberAuth = getMemberAuth();

  private readonly items = new Set<string>();
  private itemsLoaded: boolean = false;

  protected abstract basePath(): string;

  protected deserializeItem(value: any): string {
    throw new Error("Unsupported operation");
  }

  protected serializeItem(item: string): any {
    throw new Error("Unsupported operation");
  }

  protected sortOrder(item1: string, item2: string): number {
    return 0;
  }

  private listPath(): string {
    return constructListPath(this.basePath(), this.config, this.memberAuth);
  }

  async maybeLoadListItems(): Promise<void> {
    if (this.itemsLoaded) {
      return;
    }
    await this.loadListItems();
  }

  async loadListItems(): Promise<void> {
    this.itemsLoaded = false;
    this.items.clear();
    const path = this.listPath();
    const val = await this.getProvisioningContext().dbRef_getVal(path);
    let items: string[] = [];
    if (val) {
      for (const key in val) {
        items.push(key);
      }
    }
    for (const item of items) {
      this.items.add(item);
    }
    this.itemsLoaded = true;
    return Promise.resolve();
  }

  async loadListItem(id: string): Promise<string | null> {
    return id;
  }

  protected onStartObserving() {
    const path = this.listPath();
    this.getProvisioningContext().dbRef_onChildAdded(path, (result) => {
      this.items.add(result.key);
      this.observers.forEach(observer => observer.onItemChanged(result.key, ListItemChange.ADDED));
    });
    this.getProvisioningContext().dbRef_onChildChanged(path, (result) => {
      this.items.add(result.key);
      this.observers.forEach(observer => observer.onItemChanged(result.key, ListItemChange.CHANGED));
    });
    this.getProvisioningContext().dbRef_onChildRemoved(path, (result) => {
      this.items.delete(result.key);
      this.observers.forEach(observer => observer.onItemChanged(result.key, ListItemChange.REMOVED));
    });
  }

  async getOrLoadListItems(): Promise<string[]> {
    if (!this.itemsLoaded) {
      await this.loadListItems();
    }
    return Promise.resolve(this.getListItems());
  }

  getListItems(): string[] {
    if (!this.itemsLoaded) {
      console.error("Getting items but they're not loaded yet.");
    }
    return [...this.items];
  }

  getListItem(id: string): string | undefined {
    throw new Error("Unsupported operation");
  }

  getOrLoadItem(id: string): Promise<string | null> {
    throw new Error("Unsupported operation");
  }

  addListItem(item: string, rel?: Rel): Promise<void> {
    throw new Error("Unsupported operation");
  }

  deleteAllListItems(): Promise<void> {
    throw new Error("Unsupported operation");
  }

  deleteListItem(item: string): Promise<void> {
    throw new Error("Unsupported operation");
  }

  deleteListItemById(itemId: string): Promise<void> {
    throw new Error("Unsupported operation");
  }
}

export abstract class BaseListItemsLoader<T extends BaseListItem> extends AbstractListItemsLoader<T> {

  protected readonly items = new Map<string, T>(); // id -> item
  private sortedItems: T[];
  protected itemsLoaded: boolean = false;

  protected abstract basePath(): string;

  protected abstract deserializeItem(value: any): T;

  protected async onAfterItemDeserialized(item: T, val?: any): Promise<T> {
    await item._afterItemDeserialized(val);
    return item;
  }

  protected async onBeforeItemSerialized(item: T): Promise<T> {
    await item._beforeItemSerialized();
    return item;
  }

  protected abstract serializeItem(item: T): any;

  protected abstract sortOrder(item1: T, item2: T): number;

  protected listPath() {
    return constructListPath(this.basePath(), this.config, getMemberAuth());
  }

  protected listPathTemplate() {
    return constructListPath(this.basePathTemplate(), this.config, getMemberAuth());
  }

  private readonly childObservers = new DefaultObservable1<OnListItemsListener<T>>(
    id => this.onStartObservingChild(id),
    id => this.onStopObservingChild(id),
  );

  registerChildObserver(id: string, observer: OnListItemsListener<T>) {
    this.childObservers.registerObserver1(id, observer);
    this.onStartObservingChild(id);
  }

  unregisterChildObserver(id: string, observer: OnListItemsListener<T>) {
    this.childObservers.unregisterObserver1(id, observer);
    this.onStopObservingChild(id);
  }

  protected onStartObservingChild(id: string) {
    const path = this.listPath() + "/" + id;
    this.getProvisioningContext().dbRef_onVal(path, (result) => {
      const value = result.val();
      if (value) {
        this.loadListItem(value).then(item => {
          this.invalidate();
          this.items.set(item.id, item);
          this.childObservers.getObservers(id).forEach(observer => observer.onItemChanged(item, ListItemChange.CHANGED));
        });
      }
    });
  }

  protected onStopObservingChild(id: string) {

  }

  async maybeLoadListItems(): Promise<void> {
    if (this.itemsLoaded) {
      return;
    }
    await this.loadListItems();
  }

  // TODO: This method caches the current result regardless of 'options' specified. We should probably clear the cache when options changes (or cache only when no options are provided).
  async loadListItems(options?: LoadListOptions): Promise<void> {
    this.itemsLoaded = false;
    this.items.clear();
    let items = await this.fetchListItems(options);
    items = await this.onAfterItemsLoaded(items);
    for (const item of items) {
      this.items.set(item.id, item);
    }
    this.invalidate();
    this.itemsLoaded = true;
  }

  protected async fetchListItems(options?: LoadListOptions): Promise<T[]> {
    return this.config?.loadItemsRel
      ? await this.fetchListItemsInternalByLoadItemsRel(this.config.loadItemsRel, options)
      : await this.fetchListItemsInternal(options);
  }

  private listQueryConstraints(options?: LoadListOptions): QueryConstraint[] {
    if (!options) {
      return null;
    }
    const constraints: QueryConstraint[] = [];
    if (options.limitCount > 0) {
      constraints.push(limitToFirst(options.limitCount));
    }
    const obc = options.orderByChild;
    if (obc) {
      constraints.push(orderByChild(obc.key));
      if (obc.startAt) {
        constraints.push(startAt(obc.startAt));
      }
      if (obc.endAt) {
        constraints.push(endAt(obc.endAt));
      }
      if (obc.equalTo) {
        constraints.push(equalTo(obc.equalTo));
      }
    }
    return constraints;
  }

  private async fetchListItemsInternal(options?: LoadListOptions): Promise<T[]> {
    const path = this.listPath();
    const context = this.getProvisioningContext();
    const rules = this.getRules();
    if (ENV === Environment.STAGING && rules?.length > 0) {
      // In Firebase rules path format, e.g. /foo/$bar/baz, where $bar is a variable. See https://firebase.google.com/docs/database/security/core-syntax#wildcard_capture_variables
      const template = this.listPathTemplate();
      let rule: Rule = context.dbRule_get(template);
      if (!rule) {
        if (rules.length === 1) {
          rule = rules[0];
        } else {
          const read: string[] = rules.map(rule => rule.read).filter(Boolean);
          const write: string[] = rules.map(rule => rule.write).filter(Boolean);
          const validate: string[] = rules.map(rule => rule.validate).filter(Boolean);
          const indexOn: string[] = rules.map(rule => rule.indexOn).filter(Boolean).flat().filter(Boolean);
          rule = {
            read: read.length > 0 ? read.join(" && ") : undefined,
            write: write.length > 0 ? read.join(" && ") : undefined,
            validate: validate.length > 0 ? read.join(" && ") : undefined,
            indexOn: indexOn.length > 0 ? indexOn : undefined,
          }
        }
        context.dbRule_set(template, rule);
      }
    }
    const val = await context.dbRef_getVal(path, this.listQueryConstraints(options));
    let items: T[] = [];
    if (val) {
      const awaitAll: Promise<T>[] = [];
      for (const key in val) {
        let value = val[key];
        let item = this.deserializeItem(value);
        item.member = item.creator && await Members.getInstance().getOrLoadMember(item.creator);
        awaitAll.push(this.onAfterItemDeserialized(item, value));
      }
      items = await Promise.all(awaitAll);
    }
    return items;
  }

  /*
  This loads all items that match the 'loadItemsRel' relation.
  For example, if we have the following structure:

  - threads
    - __rel__
      - ptt (Participant Threads)
        - "participant-id1"
          - "thread-id1"
        - "participant-id2"
          - "thread-id1"
          - "thread-id2"
    - "thread-id1"
    - "thread-id2"

   And we load for loadItemsRel(<my-participant-id>), where <my-participant-id> === "participant-id1", then this method will,
   1. First load the __rel__ -> ptt -> "participant-id1" list (=== ["thread-id1"])
   2. Then load all items with those ids ["thread-id1"] and return that.
  */
  private async fetchListItemsInternalByLoadItemsRel(loadItemsRel: LoadItemsRel, options?: LoadListOptions): Promise<T[]> {
    const path = this.listPath() + Rel.oneToOnePathForRel(loadItemsRel());
    const val = await this.getProvisioningContext().dbRef_getVal(path);
    let keys: string[] = [];
    if (val) {
      for (const key in val) {
        keys.push(key);
      }
    }
    let items: T[] = [];
    for (const key of keys) {
      const item = await this.getOrLoadItem(key);
      if (item) {
        items.push(item);
      } else {
        console.error("Unable to load thread with id: " + key);
      }
    }
    return items;
  }

  protected async onAfterItemsLoaded(items: T[]): Promise<T[]> {
    return items;
  }

  private async onItemValueChanged(value: any | string, change: ListItemChange) {
    const loadItemsRel = this.config?.loadItemsRel;
    let item;
    if (loadItemsRel) {
      // If loadItemsRel is present, we listen for children on the __rel__ path, so 'value' is a string id.
      // HACK: Wait for change to propagate to db.
      await sleep(500);
      item = await this.loadListItem(value);
    } else {
      item = await this.deserializeListItemInternal(value);
    }
    if (!item) {
      return;
    }
    this.invalidate();
    if (change === ListItemChange.REMOVED) {
      this.items.delete(item.id);
    } else {
      this.items.set(item.id, item);
    }
    this.observers.forEach(observer => observer.onItemChanged(item, change));
  }

  protected onStartObserving() {
    const loadItemsRel = this.config?.loadItemsRel;
    const path = this.listPath() + Rel.oneToOnePathForRel(loadItemsRel?.());
    this.getProvisioningContext().dbRef_onChildAdded(path, (result) => {
      const value = result.val();
      if (value) {
        this.onItemValueChanged(value, ListItemChange.ADDED);
      }
    });
    this.getProvisioningContext().dbRef_onChildChanged(path, (result) => {
      const value = result.val();
      if (value) {
        this.onItemValueChanged(value, ListItemChange.CHANGED);
      }
    });
    this.getProvisioningContext().dbRef_onChildRemoved(path, (result) => {
      const value = result.val();
      if (value) {
        this.onItemValueChanged(value, ListItemChange.REMOVED);
      }
    });
  }

  async loadListItem(id: string): Promise<T | null> {
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + "/" + id);
    if (val) {
      return await this.deserializeListItemInternal(val);
    }
    return Promise.resolve(null);
  }

  private async deserializeListItemInternal(val): Promise<T> {
    let item = this.deserializeItem(val);
    const createdBy = item.creator || item.createdBy;
    if (createdBy) {
      item.member = await Members.getInstance().getOrLoadMember(createdBy);
      if (!item.member && BaseApp.CONTEXT.getAppConfig().loginConfig?.guestLogin) {
        item.member = createGuestMemberAuth(createdBy).member;
      }
    }
    item = await this.onAfterItemDeserialized(item, val);
    this.items.set(item.id, item);
    return item;
  }

  async getOrLoadListItems(options?: LoadListOptions): Promise<T[]> {
    // Don't think we need sortedItems check here.
    // if (!this.sortedItems) {
    if (!this.itemsLoaded) {
      await this.loadListItems(options);
    }
    return this.getListItems();
  }

  getListItems(): T[] {
    if (!this.itemsLoaded) {
      console.error("Getting items but they're not loaded yet.");
    }
    if (!this.sortedItems) {
      this.sortedItems = [...this.items.values()].sort(this.sortOrder.bind(this));
    }
    return this.sortedItems;
  }

  getListItemsCount(): number {
    return this.getListItems()?.length || 0;
  }

  getListItem(id: string): T | undefined {
    if (!id) {
      return undefined;
    }
    // Don't use getListItems() here as that will instantiate sortedItems which we want only when loading entire list.
    return [...this.items.values()].find(value => value.id === id);
  }

  protected invalidate() {
    this.sortedItems = null;
  }

  async maybeLoadItem(id: string) {
    const item = this.getListItem(id);
    if (item) {
      return;
    }
    await this.loadListItem(id);
  }

  async getOrLoadItem(id: string): Promise<T | null> {
    if (!id) {
      return null;
    }
    const item = this.getListItem(id);
    if (item) {
      return item;
    }
    return this.loadListItem(id);
  }

  async addChildItem(item: T, childPath: string, childItem: any): Promise<void> {
    const object = JSON_OBJECT.serializeObject(childItem);
    return this.getProvisioningContext().dbRef_setVal(this.listPath() + "/" + item.id + "/" + childPath, object);
  }

  async addListItem(item: T, ...rels: Rel[]): Promise<void> {

    if (item.createdAt === undefined) {
      item.createdAt = Date.now();
    }
    // BaseApp.CONTEXT.notifyAppEvent("cloud_status", CloudStatus.SAVING);
    item = await this.onBeforeItemSerialized(item);
    const object = this.serializeItem(item);
    await this.getProvisioningContext().dbRef_setVal(this.listPath() + "/" + item.id, object, ...this.updateRels(item, rels));
    // BaseApp.CONTEXT.notifyAppEvent("cloud_status", CloudStatus.SAVED);
    this.items.set(item.id, item);
    this.sortedItems = [...this.items.values()].sort(this.sortOrder);
  }

  protected async onBeforeItemDeleted(item: T): Promise<void> {
    await item.onBeforeItemDeleted();
  }

  async deleteAllListItems(): Promise<void> {
    // TODO Handle rels and onBeforeItemDeleted. How?
    await this.getProvisioningContext().dbRef_removeVal(this.listPath());
  }

  async deleteListItem(item: T, ...rels: Rel[]): Promise<void> {
    await this.onBeforeItemDeleted(item);
    await this.deleteListItemById(item.id, ...rels);
  }

  async deleteListItemById(id: string, ...rels: Rel[]): Promise<void> {
    let item: T;
    if (this.config?.updateItemRels) {
      item = await this.getOrLoadItem(id);
    }
    await this.getProvisioningContext().dbRef_removeVal(this.listPath() + "/" + id, ...this.updateRels(item, rels));
    this.items.delete(id);
    this.sortedItems = [...this.items.values()].sort(this.sortOrder);
  }

  private updateRels(item: T, rels: Rel[]): Rel[] {
    const updateRels: Rel[] = [];
    if (item && this.config?.updateItemRels) {
      updateRels.push(...this.config.updateItemRels(item));
    }
    rels = rels?.filter(Boolean);
    if (rels) {
      updateRels.push(...rels);
    }
    // Add the default rels.
    const defaultRels = item.getRels();
    if (defaultRels) {
      updateRels.push(...defaultRels);
    }
    return updateRels;
  }
}

export abstract class FixedListItemsLoader<T extends AbstractListItem> extends AbstractListItemsLoaderBase<T> {

  constructor(readonly list: T[]) {
    super();
  }

  async loadListItems(): Promise<void> {
    this.list.forEach(item => this.items.set(item.id, item));
    this.itemsLoaded = true;
  }

  async loadListItem(id: string): Promise<T | null> {
    return this.items.get(id);
  }

  protected basePath(): string {
    throw new Error("Unsupported operation");
  }

  async addListItem(item: T, ...rels): Promise<void> {
    throw new Error("Unsupported operation");
  }

  async deleteAllListItems(): Promise<void> {
    throw new Error("Unsupported operation");
  }

  async deleteListItem(item: T, ...rels): Promise<void> {
    throw new Error("Unsupported operation");
  }

  async deleteListItemById(itemId: string): Promise<void> {
    throw new Error("Unsupported operation");
  }

  protected deserializeItem(value: any): T {
    throw new Error("Unsupported operation");
  }

  protected serializeItem(item: T): any {
    throw new Error("Unsupported operation");
  }
}

export abstract class TransformListItemsLoader<V extends AbstractListItem, L extends AbstractListItemsLoader<V>, T extends AbstractListItem> extends AbstractListItemsLoaderBase<T> {

  constructor(readonly loader: L) {
    super();
  }

  protected abstract transform(item: V): Promise<T>;

  async loadListItems(options?: LoadListOptions): Promise<void> {
    this.itemsLoaded = false;
    this.items.clear();
    const original = await this.loader.getOrLoadListItems(options);
    const items: T[] = [];
    for (const item of original) {
      items.push(await this.transform(item));
    }
    for (const item of items) {
      this.items.set(item.id, item);
    }
    this.itemsLoaded = true;
  }

  async loadListItem(id: string): Promise<T | null> {
    const item = await this.loader.loadListItem(id);
    return this.transform(item);
  }

  protected basePath(): string {
    throw Error("Illegal operation.");
  }

  async addListItem(item: T, ...rels): Promise<void> {
    throw Error("Illegal operation.");
  }

  async deleteAllListItems(): Promise<void> {
    throw Error("Illegal operation.");
  }

  async deleteListItem(item: T, ...rels): Promise<void> {
    throw Error("Illegal operation.");
  }

  async deleteListItemById(itemId: string): Promise<void> {
    throw Error("Illegal operation.");
  }

  protected deserializeItem(value: any): T {
    throw Error("Illegal operation.");
  }

  protected serializeItem(item: T): any {
    throw Error("Illegal operation.");
  }
}

export class FilterListItemsLoader<T extends AbstractListItem, L extends AbstractListItemsLoader<T>> extends AbstractListItemsLoader<T> {

  constructor(readonly loader: L, readonly filter: Filter<T>) {
    super();
  }

  registerObserver(observer: OnListItemsListener<T>) {
    this.loader.registerObserver(new OnFilterListItemsListener(observer, this.filter));
  }

  unregisterObserver(observer: OnListItemsListener<T>) {
    this.loader.unregisterObserver(this.observers.find(filterObserver => (filterObserver as OnFilterListItemsListener<T>).observer === observer));
  }

  async loadListItems(options?: LoadListOptions): Promise<void> {
    await this.loader.loadListItems(options);
  }

  async getOrLoadListItems(options?: LoadListOptions): Promise<T[]> {
    const items = await this.loader.getOrLoadListItems(options);
    return items.filter(this.filter);
  }

  getListItems(): T[] {
    return this.loader.getListItems().filter(this.filter);
  }

  getListItem(id: string): T | null | undefined {
    const item = this.loader.getListItem(id);
    return this.filter(item) ? item : null;
  }

  async getOrLoadItem(id: string): Promise<T | null> {
    const item = await this.loader.getOrLoadItem(id);
    return this.filter(item) ? item : null;
  }

  protected sortOrder(item1: T, item2: T): number {
    // @ts-ignore
    return this.loader.sortOrder(item1, item2);
  }


  async loadListItem(id: string): Promise<T | null> {
    const item = await this.loader.loadListItem(id);
    return this.filter(item) ? item : null;
  }

  protected basePath(): string {
    throw Error("Illegal operation.");
  }

  async addListItem(item: T, ...rels): Promise<void> {
    await this.loader.addListItem(item, ...rels);
  }

  async deleteAllListItems(): Promise<void> {
    await this.loader.deleteAllListItems();
  }

  async deleteListItem(item: T): Promise<void> {
    await this.loader.deleteListItem(item);
  }

  async deleteListItemById(id: string): Promise<void> {
    await this.loader.deleteListItemById(id);
  }

  protected deserializeItem(value: any): T {
    throw Error("Illegal operation.");
  }

  protected serializeItem(item: T): any {
    throw Error("Illegal operation.");
  }
}

export class DefaultCompositeListItemsLoader<T extends BaseListItem, L extends BaseListItemsLoader<T>> extends AbstractListItemsLoader<T> {

  constructor(private readonly loaders: L[]) {
    super();
  }

  protected basePath(): string {
    throw new Error("Unsupported operation");
  }

  getListItem(id: string): T | undefined {
    return undefined;
  }

  getListItems(): T[] {
    return this.loaders.map(loader => loader.getListItems()).flat().sort(this.sortOrder);
  }

  async getOrLoadItem(id: string): Promise<T | null> {
    for (const loader of this.loaders) {
      const item = await loader.getOrLoadItem(id);
      if (item) {
        return item;
      }
    }
    return null;
  }

  async getOrLoadListItems(): Promise<T[]> {
    return (await Promise.all(this.loaders.map(loader => loader.getOrLoadListItems()))).flat().sort(this.sortOrder);
  }

  async loadListItems(): Promise<void> {
    await Promise.all(this.loaders.map(loader => loader.loadListItems()));
  }

  loadListItem(id: string): Promise<T | null> {
    return Promise.resolve(undefined);
  }

  async addListItem(item: T, rel: Rel | undefined): Promise<void> {
    throw new Error("Unsupported operation");
  }

  deleteAllListItems(): Promise<void> {
    throw new Error("Unsupported operation");
  }

  async deleteListItem(item: T): Promise<void> {
    throw new Error("Unsupported operation");
  }

  deleteListItemById(itemId: string): Promise<void> {
    throw new Error("Unsupported operation");
  }

  protected deserializeItem(value: any): T {
    throw new Error("Unsupported operation");
  }

  protected serializeItem(item: T): any {
    throw new Error("Unsupported operation");
  }

  protected sortOrder(item1: T, item2: T): number {
    return 0;
  }
}

@JsonObject()
export abstract class BaseTreeItem extends BaseListItem {

  static readonly ROOT_ID = "/";

  @JsonProperty()
  parentId: string;

  protected constructor(id: string, creator: string, created: number, parentId: string) {
    super(id, creator, created);
    this.parentId = parentId;
  }
}

export interface OnTreeItemsListener<T extends BaseTreeItem> {

  onItemChanged(item: T);
}

export abstract class AbstractTreeItemsLoader<T extends BaseTreeItem> extends Observable1<OnTreeItemsListener<T>> {

  constructor(protected readonly config?: TreeConfig) {
    super();
    this.overrideProvisioningContext = config?.overrideProvisioningContext;
  }

  protected overrideProvisioningContext?: ProvisioningContext;

  setOverrideProvisioningContext(overrideProvisioningContext: ProvisioningContext) {
    this.overrideProvisioningContext = overrideProvisioningContext;
  }

  protected getProvisioningContext(): ProvisioningContext {
    return this.overrideProvisioningContext || ProvisioningContext.DEFAULT;
  }
}

export abstract class BaseTreeItemsLoader<T extends BaseTreeItem> extends AbstractTreeItemsLoader<T> {

  private readonly memberAuth = getMemberAuth();

  private readonly itemsMap = new Map<string, Map<string, T>>();
  private sortedItemsMap = new Map<string, T[]>();

  abstract basePath(): string;

  relName(): string {
    return "children";
  }

  abstract deserializeItem(value: any): T;

  protected async onAfterItemDeserialized(item: T): Promise<T> {
    return item;
  }

  protected async onBeforeItemSerialized(item: T): Promise<T> {
    return item;
  }

  abstract serializeItem(item: T): any;

  abstract sortOrder(item1: T, item2: T): number;

  private childrenPath(id: string): string {
    return constructChildrenPath(this.basePath(), this.config, this.memberAuth, this.relName(), id);
  }

  private listPath(): string {
    return constructListPath(this.basePath(), this.config, this.memberAuth);
  }

  private getItemsMap(id: string): Map<string, T> {
    let map = this.itemsMap.get(id);
    if (!map) {
      map = new Map<string, T>();
      this.itemsMap.set(id, map);
    }
    return map;
  }

  async loadTreeItems(id: string): Promise<void> {
    const path = this.childrenPath(id);
    const children = await this.getProvisioningContext().dbRef_getVal(path);
    if (children) {
      for (const key in children) {
        const child = children[key];
        const value = await this.getProvisioningContext().dbRef_getVal(this.listPath() + "/" + child);
        if (value) {
          let item = this.deserializeItem(value);
          item.member = await Members.getInstance().getOrLoadMember(item.creator || item.createdBy);
          item = await this.onAfterItemDeserialized(item);
          this.getItemsMap(id).set(item.id, item);
        }
      }
    }
    return Promise.resolve();
  }

  protected onStartObserving(id: string) {
    const path = this.childrenPath(id);
    this.getProvisioningContext().dbRef_onChildAdded(path, (result) => {
      const child: string = result.val();
      if (child) {
        this.getProvisioningContext().dbRef_onValOnce(this.listPath() + "/" + child).then(value => {
          if (value) {
            this.loadListItemInternal(value).then(item => {
              this.sortedItemsMap.set(id, null);
              this.getItemsMap(id).set(item.id, item);
              this.getObservers(id).forEach(observer => observer.onItemChanged(item));
            });
          }
        });
      }
    });
  }

  private async loadListItemInternal(val) {
    let item = this.deserializeItem(val);
    item.member = await Members.getInstance().getOrLoadMember(item.creator || item.createdBy);
    item = await this.onAfterItemDeserialized(item);
    return item;
  }

  async getOrLoadTreeItems(id: string): Promise<T[]> {
    if (!this.sortedItemsMap.get(id)) {
      await this.loadTreeItems(id);
    }
    return Promise.resolve(this.getTreeItems(id));
  }

  getTreeItems(id: string): T[] {
    if (!this.sortedItemsMap.get(id)) {
      this.sortedItemsMap.set(id, [...this.getItemsMap(id)?.values()].sort(this.sortOrder));
    }
    return this.sortedItemsMap.get(id);
  }

  getTreeItem(id: string, itemId: string): T | undefined {
    return this.sortedItemsMap.get(id)?.find(value => value.id === itemId);
  }

  async getOrLoadTreeItem(id: string, itemId: string): Promise<T | undefined> {
    // TODO: Implement this.
    return undefined;
  }

  async addTreeItem(item: T): Promise<void> {
    item = await this.onBeforeItemSerialized(item);
    const object = this.serializeItem(item);
    return this.getProvisioningContext().dbRef_setVal(this.listPath() + "/" + item.id, object, new Rel(this.relName(), item.parentId, item.id, true));
  }

  async deleteAllTreeItems(): Promise<void> {
    return this.getProvisioningContext().dbRef_removeVal(this.listPath());
  }

  async deleteTreeItem(item: T): Promise<void> {
    return this.getProvisioningContext().dbRef_removeVal(this.listPath() + "/" + item.id);
  }

  async deleteTreeItemById(itemId: string): Promise<void> {
    return this.getProvisioningContext().dbRef_removeVal(this.listPath() + "/" + itemId);
  }
}

export class DefaultTreeListItemsLoader<T extends BaseTreeItem> extends AbstractListItemsLoader<T> {

  constructor(private readonly id: string, private readonly loader: BaseTreeItemsLoader<T>, config?: ListConfig<T>) {
    super(config);
  }

  registerObserver(observer: OnListItemsListener<T>) {
    observer.__treeItemsListener = new class implements OnTreeItemsListener<T> {
      onItemChanged(item: T) {
        observer.onItemChanged(item, ListItemChange.CHANGED);
      }
    }
    this.loader.registerObserver1(this.id, observer.__treeItemsListener);
  }


  unregisterObserver(observer: OnListItemsListener<T>) {
    this.loader.unregisterObserver1(this.id, observer.__treeItemsListener);
  }

  protected basePath(): string {
    return this.loader.basePath();
  }

  protected deserializeItem(value: any): T {
    return this.loader.deserializeItem(value);
  }

  protected serializeItem(item: T): any {
    return this.loader.serializeItem(item);
  }

  protected sortOrder(item1: T, item2: T): number {
    return this.loader.sortOrder(item1, item2);
  }

  async loadListItems(): Promise<void> {
    return this.loader.loadTreeItems(this.id);
  }

  async getOrLoadListItems(): Promise<T[]> {
    return this.loader.getOrLoadTreeItems(this.id);
  }

  getListItems(): T[] {
    return this.loader.getTreeItems(this.id);
  }

  getListItem(id: string): T | undefined {
    return this.loader.getTreeItem(this.id, id);
  }

  loadListItem(id: string): Promise<T | null> {
    throw new Error("Not implemented yet.");
  }

  async getOrLoadItem(id: string): Promise<T | null> {
    return this.loader.getOrLoadTreeItem(this.id, id);
  }

  async addListItem(item: T): Promise<void> {
    return this.loader.addTreeItem(item);
  }

  async deleteAllListItems(): Promise<void> {
    return this.loader.deleteAllTreeItems();
  }

  async deleteListItem(item: T): Promise<void> {
    return this.loader.deleteTreeItem(item);
  }

  deleteListItemById(itemId: string): Promise<void> {
    return this.loader.deleteTreeItemById(itemId);
  }
}

export interface AccountProfileFactory<T extends AccountProfile> {

  create(account: Account): T;
}

type AccountType = {
  name: string,
  profileFactory: AccountProfileFactory<AccountProfile>,
}

@JsonObject()
export class AccountLink {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly type: string;

  constructor(id: string, type: string) {
    this.id = id;
    this.type = type;
  }
}

// Objects of this type are always stored in the MAIN provisioning context, under the MAIN memberId (which is different from those under each provisioningId).
@JsonObject()
export class Account extends BaseListItem {

  private static readonly accountTypesByName = new Map<string, AccountType>();

  static registerAccountType(type: AccountType): void {
    this.accountTypesByName.set(type.name, type);
  }

  member: Member;

  @JsonProperty()
  readonly type: string;

  @JsonProperty()
  readonly memberId: string;

  static createNew(type: string, member: Member): Account {
    if (!this.accountTypesByName.has(type)) {
      throw new Error(`Account type "${type}" not registered.`);
    }
    const account = new Account(md5_uuid(), Date.now(), type, member.memberId);
    account.member = member;
    return account;
  }

  constructor(id: string, created: number, type: string, memberId: string) {
    super(id, null, created);
    this.type = type;
    this.memberId = memberId;
  }

  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    if (this.memberId) {
      this.member = await Members.getInstance().getOrLoadMember(this.memberId);
    }
  }

  private profile: AccountProfile;

  async loadProfile<T extends AccountProfile>(create?: boolean): Promise<void> {
    const profile = Account.accountTypesByName.get(this.type)?.profileFactory.create(this);
    if (!profile) {
      return undefined;
    }
    await profile.load();
    if (!profile.isLoaded()) {
      if (!create) {
        return null;
      }
      await profile.save();
    }
    this.profile = profile as T;
  }

  getProfile<T extends AccountProfile>(): T | undefined {
    return this.profile as T;
  }

  async getOrLoadProfile<T extends AccountProfile>(create?: boolean): Promise<T | null | undefined> {
    if (!this.profile) {
      await this.loadProfile(create);
    }
    return this.profile as T;
  }
}

export interface AccountContextTrait extends ContextTrait {

  getAccount(): Account;

  getAccountByType(type: string): Account | null | undefined;

  getOrLoadAccounts(): Promise<Account[]>;
}

// Should not be used directly, only via MemberAuth.getOrLoadAccount(s)
export class _Accounts extends BaseListItemsLoader<Account> {

  private static instance: _Accounts;

  static getInstance(): _Accounts {
    if (!this.instance) {
      this.instance = new _Accounts();
    }
    return this.instance;
  }

  private constructor() {
    super({
      shared: false,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined
    });
  }

  protected basePath(): string {
    return "accounts";
  }

  protected deserializeItem(value: any): Account {
    return JSON_OBJECT.deserializeObject(value, Account);
  }

  protected serializeItem(item: Account): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Account, item2: Account): number {
    return item1.created - item2.created;
  }
}

@JsonObject()
export abstract class AccountProfile extends TypedObject {

  @JsonProperty()
  readonly id: string;

  @JsonProperty()
  readonly type: string;

  constructor(id: string, type: string) {
    super(null, null);
    this.id = id;
    this.type = type;
  }

  protected basePath(): string | null {
    return "accountProfiles";
  }

  protected objectConfig(): ObjectConfig | null {
    return {
      shared: true,
      overrideProvisioningContext: BaseApp.CONTEXT.getAppConfig()?.loginConfig?.provisioning?.membersUseSharedProvisioningContext ? ProvisioningContext.MAIN : undefined,
    };
  }
}

export type PresetsKeyType = string;

export class PresetsKey {

  constructor(readonly type: PresetsKeyType, readonly region?: Region) {
  }

  toString(): string {
    return [this.type, this.region?.country, this.region?.country ? this.region?.locale : null].filter(Boolean).join("/");
  }
}

export function $PRK(type: PresetsKeyType, region?: Region) {
  return new PresetsKey(type, region);
}

@JsonObject()
export abstract class Preset extends AbstractListItem {

  @JsonProperty()
  readonly sortOrderNumber: number;

  @JsonProperty()
  noneditable?: boolean;

  constructor(id: string, sortOrderNumber: number = 0) {
    super(id);
    this.sortOrderNumber = sortOrderNumber;
  }

  setNonEditable(): this {
    this.noneditable = true;
    return this;
  }

  toKey(): string {
    return this.id;
  }

  abstract toString(): string;
}

@JsonObject()
export class PresetText extends Preset {

  @JsonProperty()
  @FormGen({name: "Key", type: "string", slug: true})
  readonly key: string;

  @JsonProperty()
  @FormGen({name: "Text", type: "string"})
  readonly text: string;

  constructor(key: string, text: string, sortOrderNumber: number = 0) {
    super(md5(key), sortOrderNumber);
    this.key = key;
    this.text = text;
  }

  toKey(): string {
    return this.key;
  }

  toString(): string {
    return this.text;
  }
}

// Key is the same as text. Should only be used for non-translatable strings (e.g. medicine names)
export function $PRTXT0(text: string, sortOrder: number = 0): PresetText {
  return new PresetText(text, text, sortOrder);
}

export function $PRTXT(key: string, text: string, sortOrder: number = 0): PresetText {
  return new PresetText(key, text, sortOrder);
}

export enum PresetsListItemType {
  SELECT = "select",
  SEARCH = "search",
}

export const PRESETS_LIST_ITEM_TYPES_KTS = new KeyTextStrings([
  $KTS(PresetsListItemType.SELECT, "Select"),
  $KTS(PresetsListItemType.SEARCH, "Search"),
]);

export class PresetsListItem extends BaseListItem {

  constructor(readonly value: KeyTextString, readonly type: PresetsListItemType) {
    super(md5_uuid(), null, null);
  }
}

export abstract class BasePresetsList extends FixedListItemsLoader<PresetsListItem> {

  protected constructor(list: PresetsListItem[]) {
    super(list);
  }

  protected basePath(): string {
    throw new Error("Illegal operation.");
  }

  protected sortOrder(item1: PresetsListItem, item2: PresetsListItem): number {
    return item1.value.text.localeCompare(item2.value.text);
  }
}

@JsonObject()
export abstract class CustomPreset<T extends Preset> extends BaseListItem {

  @JsonProperty()
  @FormGen({name: "", type: "object"})
  readonly object: T;

  constructor(id: string, creator: string, created: number, object: T) {
    super(id, creator, created);
    this.object = object;
  }
}

export abstract class CustomPresets<T extends CustomPreset<any>> extends BaseListItemsLoader<T> {

  constructor(readonly presetsName: string, private readonly type: Type<T>) {
    super({shared: true, scopeRoot: true});
  }

  protected basePath(): string {
    return `presets/${this.presetsName}`;
  }

  protected deserializeItem(value: any): T {
    return JSON_OBJECT.deserializeObject(value, this.type);
  }

  protected serializeItem(item: T): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: T, item2: T): number {
    return item1.created - item2.created;
  }
}

export interface PresetsFactory<T extends Preset = PresetText> {

  // initialize(): Promise<void>;

  createPresets(key: PresetsKey): Presets<T>;
}

export abstract class AbstractPresetsFactory<T extends Preset, C extends CustomPreset<T>, L extends BaseListItemsLoader<C>> {

  constructor(readonly loader: L) {
  }

  async initialize(): Promise<void> {
    await this.loader.getOrLoadListItems();
  }

  abstract createPresets(key: PresetsKey): Presets<T>;
}

export abstract class Presets<T extends Preset = PresetText> extends FixedListItemsLoader<T> {

  private static readonly factories = new Map<PresetsKeyType, PresetsFactory<any>>();

  static registerFactory<T extends Preset = PresetText>(type: PresetsKeyType, factory: PresetsFactory<T>): void {
    this.factories.set(type, factory);
  }

  static getFactories(): PresetsFactory[] {
    return [...this.factories.values()];
  }

  private static readonly instances = new Map<string, Presets<any>>();

  static getInstance<T extends Preset = PresetText>(key: PresetsKey): Presets<T> {
    let keyString = key.toString();
    let instance = this.instances.get(keyString);
    if (!instance) {
      const factory = this.factories.get(key.type);
      if (factory) {
        instance = factory.createPresets(key);
        this.instances.set(keyString, instance);
      }
    }
    return instance;
  }

  static findPreset(presetsKey: PresetsKeyType, presetKey: string): PresetText | undefined {
    const presets = Presets.getInstance(new PresetsKey(presetsKey)).getListItems();
    return presets?.find(preset => preset.key === presetKey);
  }

  constructor(readonly key: PresetsKey, list: T[], readonly description?: string) {
    super(list);
  }

  protected basePath(): string {
    return "__global__/presets/" + this.key.toString();
  }

  findText(key: string): string {
    return this.getListItems().find(item => item["key"] === key)?.["text"];
  }
}

export class DefinedPresets<T extends Preset = PresetText> extends Presets<T> {

  private _sortAlphabetically: boolean;

  constructor(key: PresetsKey, list: T[], description?: string) {
    super(key, list, description);
  }

  sortAlphabetically(): DefinedPresets<T> {
    this._sortAlphabetically = true;
    return this;
  }

  protected sortOrder(item1: T, item2: T): number {
    if (this._sortAlphabetically) {
      return item1['text']?.localeCompare(item2['text']);
    }
    return item1.sortOrderNumber - item2.sortOrderNumber;
  }
}

export class PresetTexts extends Presets {

  protected deserializeItem(value: any): PresetText {
    return JSON_OBJECT.deserializeObject(value, PresetText);
  }

  protected serializeItem(item: PresetText): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: PresetText, item2: PresetText): number {
    return item1.sortOrderNumber - item2.sortOrderNumber;
  }
}

export class MenuOption {
  static readonly OPTIONS_ITEM_TYPE_MASK = 0x3;
  static readonly OPTIONS_ITEM_TYPE_BUTTON_FLAG = 1;
  static readonly OPTIONS_ITEM_TYPE_TEXT_FLAG = 2;
  static readonly OPTIONS_ITEM_TYPE_ACTION_FLAG = 3;

  static createFromAction(action: Action) {
    return new MenuOption(null, action.text, action.iconType, MenuOption.OPTIONS_ITEM_TYPE_BUTTON_FLAG, action.onClick);
  }

  constructor(readonly id: string, readonly text: string, readonly iconType: typeof SvgIcon = null, readonly flags: number = 0, readonly onClick?: (event) => void) {
  }
}

export type Sync = {
  syncId: string,
  type: string,
  from?: string,
  time: number,
  duration?: number,
  payload?: any,
}

export interface Copyable<T> {
  copy(): T;
}

export class LocalFile {

  constructor(readonly blob: Blob, readonly name: string, readonly size: number) {
  }
}

export interface BlobType {

  getMimeType(): string;

  getBlobId(): string;
}

export function DisplayName(firstname?: string, lastname?: string, prefix?: string) {
  return [prefix, firstname, lastname].filter(Boolean).join(" ");
}

export function UserDisplayName(user?: User) {
  return DisplayName(user?.firstname, user?.lastname);
}

export function UserFriendlyName(user?: User) {
  return DisplayName(user?.firstname);
}

export function ProfilePhoto(profilePhoto: string, defaultImage?: string) {
  return profilePhoto || defaultImage || BaseApp.CONTEXT.getAppConfig().defaultUserImage || defaultUserImage;
}

export function UserProfilePhoto(user?: User, defaultImage?: string) {
  return ProfilePhoto(user?.profilePhoto, defaultImage);
}

export function UnknownUser(uid: string): User {
  return {
    uid: uid,
    firstname: "[ Unknown ]",
    lastname: "",
  } as User;
}

export class UserCache {

  private static readonly instance = new UserCache();

  private cache: Map<string, User> = new Map<string, User>();

  static getInstance(): UserCache {
    return this.instance;
  }

  setUser(uid: string, user: User): void {
    this.cache.set(uid, user);
  }

  getCachedUser(uid: string): User {
    return this.cache.get(uid);
  }

  async getUser(uid: string): Promise<User> {
    let cached = this.cache.get(uid);
    if (cached) {
      return Promise.resolve(cached);
    }
    const userRef = dbRef("users/" + uid);
    const result = await get(userRef);
    if (result.exists()) {
      const cached = JSON_OBJECT.deserializeObject(result.val(), User);
      this.cache.set(uid, cached);
      return Promise.resolve(cached);
    }
    return Promise.resolve(UnknownUser(uid));
  }

  async loadUsers(): Promise<User[]> {
    const usersRef = dbRef("users");
    const result = await get(usersRef);
    if (result.exists()) {
      const users: User[] = [];
      let val = result.val();
      for (const key in val) {
        let value = val[key];
        const user = JSON_OBJECT.deserializeObject(value, User);
        users.push(user);
      }
      return Promise.resolve(users);
    }
    return Promise.resolve([]);
  }
}

export type FocusTabItem = {
  path: string,
  text: string,
}

export type FocusTabs = {
  items: FocusTabItem[],
  selectedItem: FocusTabItem | null,
  setSelectedItem: (item: FocusTabItem | null) => void,
}

export type FocusMode = {
  text: string;
  // tabs?: FocusTabs,
  onClose?: () => void;
}

export type Renderable = ReactElement | ((pathProps: PathProps) => ReactElement);

export function Render(renderable: Renderable, pathProps: PathProps): ReactElement | null | undefined {
  if (!renderable) {
    return null;
  }
  if (typeof renderable === "function") {
    return (renderable as (pathProps: PathProps) => ReactElement)(pathProps);
  }
  return renderable as ReactElement;
}

export enum TabOptionItemType {
  BUTTON = "button",
  TOGGLE = "toggle",
}

export type Callable<A, T> = (arg: A) => T;
export type Callable1<A, B, T> = (arg1: A, arg2: B) => T;
export type Predicate<T> = (item: T) => T;
export type Filter<T> = (item: T) => boolean;

export class TabOptionItem {

  type: TabOptionItemType = TabOptionItemType.BUTTON;

  private selected: boolean = false;

  iconType: typeof SvgIcon;

  fontSize: string;

  colorValue: ColorValue;

  onDoubleClicked?: (item: TabOptionItem) => void;

  constructor(
    readonly id: string,
    readonly text: string,
    readonly onSelectionChanged?: (item: TabOptionItem, selected: boolean) => void,
    readonly onClicked?: (item: TabOptionItem) => boolean) {
  }

  setOnDoubleClicked(onDoubleClicked: (item: TabOptionItem) => boolean): this {
    this.onDoubleClicked = onDoubleClicked;
    return this;
  }

  setIconType(iconType: typeof SvgIcon): TabOptionItem {
    this.iconType = iconType;
    return this;
  }

  setFontSize(fontSize: string): TabOptionItem {
    this.fontSize = fontSize;
    return this;
  }

  setColorValue(colorValue: ColorValue): TabOptionItem {
    this.colorValue = colorValue;
    return this;
  }

  isSelected(): boolean {
    return this.selected;
  }

  setSelected(selected: boolean): TabOptionItem {
    this.selected = selected;
    this.onSelectionChanged?.(this, selected);
    return this;
  }
}

export class TabOptionItemGroup {

  constructor(readonly items: TabOptionItem[], private selectedIndex: number = 0, readonly layout: "expand" | "collapse" = "expand") {
    this.setSelectedIndex(selectedIndex);
  }

  getSelectedItem(): TabOptionItem {
    return this.items[this.selectedIndex];
  }

  setSelectedIndex(selectedIndex: number) {
    this.selectedIndex = selectedIndex;
    this.items.map((item, index) => item.setSelected(selectedIndex === index));
  }
}

export class TabOptions {

  alwaysShow?: boolean;

  constructor(readonly options: (TabOptionItem | TabOptionItemGroup)[], readonly onTabSelected?: (tab: string) => void, readonly onTabClosed?: (tab: string) => boolean, readonly onTabDoubleClicked?: (tab: string) => void) {
  }
}

export class ButtonState {

  constructor(readonly disabled?: boolean, readonly selected?: boolean) {
  }

  toggleSelected(): ButtonState {
    return new ButtonState(this.disabled, !this.selected);
  }
}

export type CreateActionsArgs<T extends (BaseListItem | TypedObject) = any, R = any> = {
  path: PathProps,
  data: T,
  rowValue: any,
  container: TableDataContainer<T, R>,
}

export type CreateActions = (event, args: CreateActionsArgs) => Action[];

export enum ActionType {
  SPACE = "space",
  SEPARATOR = "separator",
  BUTTON = "button",
  BUTTON_TOOLBAR = "button_toolbar",
  BUTTON_TOOLBAR_SELECT = "button_toolbar_select",
  BUTTON_ICON = "button_icon",
  BUTTON_SLIDER = "button_slider",
  GROUP = "group",
  SELECT = "select",
  POPOVER = "popover",
  ACTIONS_LIST_POPOVER = "actions_list_popover",
  ACTIONS_LIST_POPOVER_ICON = "actions_list_popover_icon",
}

export class ActionBase {
  readonly type: ActionType;

  tooltipText?: string;

  isSelectedFn?: () => boolean;
  isDisabledFn?: () => boolean;
  isHiddenFn?: () => boolean;

  constructor(type: ActionType) {
    this.type = type;
  }

  setIsSelectedFn(isSelectedFn: () => boolean) {
    this.isSelectedFn = isSelectedFn;
    return this;
  }

  setIsDisabledFn(isDisabledFn: () => boolean) {
    this.isDisabledFn = isDisabledFn;
    return this;
  }

  setIsHiddenFn(isHiddenFn: () => boolean) {
    this.isHiddenFn = isHiddenFn;
    return this;
  }

  setTooltipText(text: string): ActionIcon {
    this.tooltipText = text;
    return this;
  }
}

abstract class ActionButtonBase extends ActionBase {

  constructor(type: ActionType, readonly onClick?: (event?: any, ...args: any[]) => void, readonly iconType?: typeof SvgIcon, readonly disabled?: boolean) {
    super(type);
  }
}

export class Action extends ActionButtonBase {

  tag?: any;
  selected?: boolean;
  destructive?: boolean;
  secondary?: boolean;
  display?: "button" | "icon";
  variant?: "text" | "contained" | "outlined";
  orientation?: "horizontal" | "vertical";
  iconFlipVertical?: boolean;
  badgeText?: string;
  keyboardShortcut?: string;
  dropdown?: boolean;
  iconColor?: string;
  customIconRenderer?: () => ReactElement;

  constructor(readonly text: string, onClick?: (event?: any, ...args: any[]) => void, iconType?: typeof SvgIcon, readonly iconUrl?: string, readonly iconify?: string | ReactElement, disabled?: boolean) {
    super(ActionType.BUTTON, onClick, iconType, disabled);
  }

  setOrientation(orientation: "horizontal" | "vertical") {
    this.orientation = orientation;
    return this;
  }

  setIconFlipVertical() {
    this.iconFlipVertical = true;
    return this;
  }

  setCustomIconRenderer(renderer: () => ReactElement) {
    this.customIconRenderer = renderer;
    return this;
  }

  setSelected(selected: boolean): Action {
    this.selected = selected;
    return this;
  }

  setDisplay(display: "button" | "icon" | null | undefined): Action {
    this.display = display;
    return this;
  }

  setVariant(variant: "text" | "contained" | "outlined"): Action {
    this.variant = variant;
    return this;
  }

  setBadgeText(badgeText: string): Action {
    this.badgeText = badgeText;
    return this;
  }

  setKeyboardShortcut(keyboardShortcut: string): Action {
    this.keyboardShortcut = keyboardShortcut;
    return this;
  }

  setIconColor(iconColor: string): Action {
    this.iconColor = iconColor;
    return this;
  }

  makeDropdown(): Action {
    this.dropdown = true;
    return this;
  }

  makeSecondary(): Action {
    this.secondary = true;
    return this;
  }

  makeDestructive(): Action {
    this.destructive = true;
    return this;
  }
}

export const ACTION_OK = new Action("OK", () => {
});

export class ActionIcon extends ActionButtonBase {

  static fromAction(action: Action): ActionIcon {
    return new ActionIcon(action.onClick, action.iconType, action.disabled).setTooltipText(action.text);
  }

  constructor(onClick: (event?: any, ...args: any[]) => void, iconType: typeof SvgIcon, disabled?: boolean) {
    super(ActionType.BUTTON_ICON, onClick, iconType, disabled);
  }
}

export class ActionArg<T> extends Action {

  constructor(text: string, onClick: (event?: any, arg?: T) => void, iconType?: typeof SvgIcon, iconUrl?: string, iconify?: string, disabled?: boolean) {
    super(text, onClick, iconType, iconUrl, iconify, disabled);
  }
}

export class ActionTool extends Action {

  constructor(text: string, onClick?: (event?: any, ...args: any[]) => void, iconType?: typeof SvgIcon, iconUrl?: string, iconify?: string, disabled?: boolean) {
    super(text, onClick, iconType, null, null, disabled);
    this.setOrientation("vertical");
  }

  setOrientation(orientation: "horizontal" | "vertical") {
    this.orientation = orientation;
    return this;
  }
}

export class ActionSlider extends ActionButtonBase {

  constructor(text: string, onClick?: (event?: any, ...args: any[]) => void, iconType?: typeof SvgIcon, disabled?: boolean) {
    super(ActionType.BUTTON_SLIDER, onClick, iconType);
    this.setTooltipText(text);
  }
}

export class ActionToolbar extends ActionButtonBase {

  constructor(text: string, onClick?: (event?: any, ...args: any[]) => void, iconType?: typeof SvgIcon, disabled?: boolean) {
    super(ActionType.BUTTON_TOOLBAR, onClick, iconType);
    this.setTooltipText(text);
  }
}

export class ActionToolbarSelect extends ActionButtonBase {

  _selectedActionIndex?: number;

  constructor(readonly actions: ActionBase[], text: string, onClick?: (event?: any, ...args: any[]) => void, iconType?: typeof SvgIcon, disabled?: boolean) {
    super(ActionType.BUTTON_TOOLBAR_SELECT, onClick, iconType);
    this.setTooltipText(text);
  }
}

class ActionSeparator extends ActionBase {
  constructor() {
    super(ActionType.SEPARATOR);
  }
}

export const ACTION_SEPARATOR = new ActionSeparator();

class ActionSpace extends ActionBase {
  constructor() {
    super(ActionType.SPACE);
  }
}

export const ACTION_SPACE = new ActionSpace();


export enum ActionGroupMode {
  NO_SELECT,
  SINGLE_SELECT,
  SINGLE_SELECT_AUTO, // Auto select the first option.
  MULTISELECT,
}

export class ActionGroup extends ActionBase {

  constructor(readonly actions: ActionBase[], readonly label?: string | ReactNode, readonly mode?: ActionGroupMode, readonly iconType?: typeof SvgIcon) {
    super(ActionType.GROUP);
    if (mode === ActionGroupMode.SINGLE_SELECT_AUTO) {
      this.filterButtons()[0].selected = true;
    }
  }

  filterButtons(): Action[] {
    return this.actions.filter(base => base.type === ActionType.BUTTON).map(base => base as Action);
  }

  onClickOverride(): (action: Action, event) => void | null {
    if (this.mode === ActionGroupMode.SINGLE_SELECT || this.mode === ActionGroupMode.SINGLE_SELECT_AUTO) {
      return (action: Action, event) => {
        this.filterButtons().forEach(button => button.selected = false);
        action.selected = true;
        action.onClick?.();
      };
    }
    if (this.mode === ActionGroupMode.MULTISELECT) {
      return (action: Action, event) => {
        action.selected = !action.selected;
        action.onClick?.();
      };
    }
    return null;
  }
}

export class ActionSelect extends ActionBase {

  constructor(readonly actions: ActionBase[], readonly text: string, readonly iconType?: typeof SvgIcon) {
    super(ActionType.SELECT);
  }

  filterButtons(): Action[] {
    return this.actions.filter(base => base.type === ActionType.BUTTON).map(base => base as Action);
  }
}

export class ActionPopover extends ActionBase {

  constructor(readonly render: () => ReactElement, readonly text: string, readonly iconType?: typeof SvgIcon) {
    super(ActionType.POPOVER);
  }

  protected hidePopover() {
    BaseApp.CONTEXT.hidePopover();
  }
}

abstract class ActionActionsListPopoverBase extends ActionBase {

  variant?: "text" | "contained" | "outlined";

  constructor(type: ActionType, readonly actions: ActionBase[], readonly text: string, readonly iconType?: typeof SvgIcon) {
    super(type);
  }

  protected hidePopover() {
    BaseApp.CONTEXT.hidePopover();
  }

  setVariant(variant: "text" | "contained" | "outlined"): ActionActionsListPopover {
    this.variant = variant;
    return this;
  }
}

export class ActionActionsListPopover extends ActionActionsListPopoverBase {

  constructor(actions: ActionBase[], text: string, iconType?: typeof SvgIcon) {
    super(ActionType.ACTIONS_LIST_POPOVER, actions, text, iconType);
  }
}

export class ActionActionsListPopoverIcon extends ActionActionsListPopoverBase {

  constructor(actions: ActionBase[], iconType: typeof SvgIcon) {
    super(ActionType.ACTIONS_LIST_POPOVER_ICON, actions, null, iconType);
  }
}

export type WidgetItemLink = {
  to: string,
  text: string,
}

export type WidgetItem = {
  disabled?: boolean,
  title: string,
  iconType?: typeof SvgIcon,
  highlightColor?: string,
  links?: WidgetItemLink[],
  actions?: ActionArg<PathProps>[],
  render?: (pathProps: PathProps) => ReactElement,
  emptyConfig?: EmptyConfig,
}

export type EmptyConfig = {
  iconType?: typeof SvgIcon,
  iconify?: string | ReactElement,
  iconUrl?: string,
  title: string,
  text?: string,
  action?: Action,
  altAction?: Action,
}

@JsonObject()
export class FirebaseOverlay {

  @JsonProperty()
  apiKey?: string;
  @JsonProperty()
  authDomain?: string;
  @JsonProperty()
  databaseURL?: string;
  @JsonProperty()
  projectId?: string;
  @JsonProperty()
  storageBucket?: string;
  @JsonProperty()
  messagingSenderId?: string;
  @JsonProperty()
  appId?: string;
  @JsonProperty()
  measurementId?: string;
}

@JsonObject()
export class AppOverlay {

  @JsonProperty()
  name?: string;
  @JsonProperty()
  icon?: string;
  @JsonProperty()
  logo?: string;
  @JsonProperty()
  stamp?: string;
  @JsonProperty()
  themeOptions?: any;
  @JsonProperty()
  defaultUserImage?: string;
  @JsonProperty()
  firebase?: FirebaseOverlay;
}

@JsonObject()
export class LoginCredentials {

  @JsonProperty()
  email: string;
  @JsonProperty()
  password: string;
}

@JsonObject()
export class AppletConfig {

  @JsonProperty()
  autoLogin?: LoginCredentials;
}

export interface AppletContext extends Context {

  getAppletId(): string;

  getAppletConfig(): AppletConfig;
}

@JsonObject()
export class ProvisionedAppConfig {

  @JsonProperty()
  provisioningId: string;
}

export interface ProvisionedAppContext extends Context {

  getProvisionedAppId(): string;

  getProvisionedAppConfig(): ProvisionedAppConfig | null;
}

@JsonObject()
export class IdRequest {

  @JsonProperty()
  host: string;

  @JsonProperty()
  autoClose?: boolean;

  @JsonProperty()
  autoCloseTimeout?: number;
}

@JsonObject()
export class IdResponse {

  @JsonProperty()
  autoLogin: LoginCredentials;
}

@JsonObject()
export class ProvisioningConfig {

  @JsonProperty()
  provisioningId: string;
}

@JsonObject()
export class PluginConfig {

  @JsonProperty()
  app?: AppOverlay;

  @JsonProperty()
  autoLogin?: LoginCredentials;
}

@JsonObject()
export abstract class BaseWindowMessage {

  @JsonProperty()
  readonly name: string;

  constructor(name: string) {
    this.name = name;
  }
}

@JsonObject()
export class ResizeWindowMessage extends BaseWindowMessage {

  @JsonProperty()
  width: number;

  @JsonProperty()
  height: number;

  constructor() {
    super("resize");
  }
}

export interface PluginContext extends Context {

  getPluginId(): string;

  getPluginConfig(): PluginConfig;
}

export function PluginIconUrl(manifest: PluginManifest) {
  return manifest.iconUrl ? manifest.pluginUrl + "/" + manifest.iconUrl : null;
}

export function PluginIconType(manifest: PluginManifest) {
  return findIcon(manifest.iconType);
}

@JsonObject()
export class PluginManifestGroup {

  @JsonProperty()
  name: string;

  @JsonProperty()
  description: string;

  @JsonProperty({name: "icon_type"})
  iconType: string;
}

@JsonObject()
export class PluginManifest {

  // Not serialized
  pluginUrl: string;

  @JsonProperty({name: "plugin_id"})
  pluginId: string;

  @JsonProperty({name: "plugin_type"})
  pluginType?: "app" | "patient" | "case" | string;

  // Only set when pluginType === "case"
  @JsonProperty({name: "case_type"})
  caseType?: string;

  @JsonProperty()
  name: string;

  @JsonProperty()
  description: string;

  @JsonProperty({name: "icon_url"})
  iconUrl: string;

  @JsonProperty({name: "icon_type"})
  iconType: string;

  @JsonProperty({name: "website_url"})
  websiteUrl?: string;

  @JsonProperty({name: "support_email"})
  supportEmail?: string;

  @JsonProperty({name: "support_url"})
  supportUrl?: string;

  @JsonProperty({name: "privacy_policy_url"})
  privacyPolicyUrl?: string;

  @JsonProperty({name: "plugin_group"})
  pluginGroup: PluginManifestGroup;
}

@JsonObject()
export class MailboxBase extends BaseListItem {

  static readonly DEFAULT_ID = "__default__";

  private static defaultInstance: MailboxBase;

  static getDefaultInstance(): MailboxBase {
    if (!this.defaultInstance) {
      this.defaultInstance = new MailboxBase(this.DEFAULT_ID);
    }
    return this.defaultInstance;
  }

  /*protected*/
  constructor(id: string) {
    super(id, null, null);
  }
}

export class MailboxBases extends BaseListItemsLoader<MailboxBase> {

  static instance: MailboxBases;

  static getInstance(): MailboxBases {
    if (!this.instance) {
      this.instance = new MailboxBases();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "mailboxes";
  }

  protected async onAfterItemsLoaded(items: MailboxBase[]): Promise<MailboxBase[]> {
    return [
      MailboxBase.getDefaultInstance(),
      ...items,
    ];
  }

  getListItems(): MailboxBase[] {
    return [
      MailboxBase.getDefaultInstance(),
    ];
  }

  protected deserializeItem(value: any): MailboxBase {
    return JSON_OBJECT.deserializeObject(value, MailboxBase);
  }

  protected serializeItem(item: MailboxBase): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: MailboxBase, item2: MailboxBase): number {
    return 0;
  }
}

export enum FileType {
  TYPE_OBJECT = 0,
  TYPE_GROUP = 1,
  TYPE_LINK = 2,
}

@JsonObject()
export class FileMetadata {

  @FormGen({name: "Name", type: "string"})
  @JsonProperty()
  name: string;
}

@JsonObject()
export abstract class File<T extends FileMetadata = FileMetadata> extends BaseTreeItem {

  protected parent?: FileGroup;

  @JsonProperty()
  metadata: T;

  @JsonProperty()
  readonly type: FileType;

  @JsonProperty()
  editor?: string;

  @JsonProperty()
  edited?: number;

  @JsonProperty()
  trashed?: boolean;

  constructor(
    id: string,
    creator: string,
    created: number,
    type: FileType,
    parentId: string,
    editor?: string,
    edited?: number,
  ) {
    super(id, creator, created, parentId);
    this.type = type;
    this.editor = editor;
    this.edited = edited;
  }
}

@JsonObject()
export class FileObjectMetadata extends FileMetadata {

  @FormGen({
    name: "Type",
    type: "string",
  })
  @JsonProperty()
  mimeType: string = MimeTypeMap.MimeType_text_plain;

  @JsonProperty()
  size: number;

  @JsonProperty()
  downloadUrl: string;
}

@JsonObject()
export class FileObject extends File<FileObjectMetadata> {

  static createNew(creator: string, parentId?: string): FileObject {
    return new FileObject(md5_uuid(), creator, Date.now(), parentId);
  }

  constructor(
    id: string,
    creator: string,
    created: number,
    parentId: string,
  ) {
    super(id, creator, created, FileType.TYPE_OBJECT, parentId);
    this.metadata = new FileObjectMetadata();
  }
}

@JsonObject()
export class FileGroupMetadata extends FileMetadata {

  file: File[];

  @JsonProperty()
  fileIds: string[];

  @FormGen({name: "Color", type: "color"})
  @JsonProperty()
  color: string;
}

@JsonObject()
export class FileGroup extends File<FileGroupMetadata> {

  static createNew(creator: string, parentId?: string): FileGroup {
    return new FileGroup(md5_uuid(), creator, Date.now(), parentId);
  }

  constructor(
    id: string,
    creator: string,
    created: number,
    parentId: string,
  ) {
    super(id, creator, created, FileType.TYPE_GROUP, parentId);
    this.metadata = new FileGroupMetadata();
  }
}

@JsonObject()
class FileLinkMetadata extends FileMetadata {

  @JsonProperty()
  fileId: string;
}

@JsonObject()
export class FileLink extends File<FileLinkMetadata> {

  constructor(
    id: string,
    creator: string,
    created: number,
    parent: string,
  ) {
    super(id, creator, created, FileType.TYPE_LINK, parent);
    this.metadata = new FileLinkMetadata();
  }
}

export class Files extends BaseListItemsLoader<File> {

  private static instance: Files;

  static getInstance(): Files {
    if (!this.instance) {
      this.instance = new Files();
    }
    return this.instance;
  }

  protected basePath(): string {
    return "files";
  }

  async loadListItems(): Promise<void> {
    throw Error("Not supported. We should never try to list all files.");
  }

  async getOrLoadListItems(): Promise<File[]> {
    throw Error("Not supported. We should never try to list all files.");
  }

  getListItems(): File[] {
    throw Error("Not supported. We should never try to list all files.");
  }

  deserializeItem(value: any): File {
    switch (value.type as FileType) {
      case FileType.TYPE_OBJECT:
        return JSON_OBJECT.deserializeObject(value, FileObject);
      case FileType.TYPE_GROUP:
        return JSON_OBJECT.deserializeObject(value, FileGroup);
    }
    return null;
  }

  serializeItem(item: File): any {
    return JSON_OBJECT.serializeObject(item);
  }

  sortOrder(item1: File, item2: File): number {
    return (item1.edited || item1.created) - (item2.edited || item2.created);
  }
}

export enum FilesViewType {
  DEFAULT = "default",
  TRASH = "trash",
}

export class FilesTreeListLoader<T extends File = File> extends DefaultTreeListItemsLoader<T> {

  private viewType?: FilesViewType;

  constructor(id: string, loader: BaseTreeItemsLoader<T>) {
    super(id, loader, {shared: true});
  }

  setViewType(viewType: FilesViewType): FilesTreeListLoader<T> {
    this.viewType = viewType;
    return this;
  }

  getListItems(): T[] {
    const files = super.getListItems();
    switch (this.viewType) {
      default:
      case FilesViewType.DEFAULT:
        return files.filter(file => !file.trashed);
      case FilesViewType.TRASH:
        return files.filter(file => file.trashed);
    }
  }
}

export class FilesTreeLoader extends BaseTreeItemsLoader<File> {

  private static instance: FilesTreeLoader;
  private static listInstanceMap = new Map<string, FilesTreeListLoader>();

  static getListInstance(id: string): FilesTreeListLoader {
    if (!this.instance) {
      this.instance = new FilesTreeLoader();
    }
    let listInstance = this.listInstanceMap.get(id);
    if (!listInstance) {
      listInstance = new FilesTreeListLoader(id, this.instance);
      this.listInstanceMap.set(id, listInstance);
    }
    return listInstance;
  }

  basePath(): string {
    return "files";
  }

  deserializeItem(value: any): File {
    switch (value.type as FileType) {
      case FileType.TYPE_OBJECT:
        return JSON_OBJECT.deserializeObject(value, FileObject);
      case FileType.TYPE_GROUP:
        return JSON_OBJECT.deserializeObject(value, FileGroup);
    }
    return null;
  }

  serializeItem(item: File): any {
    return JSON_OBJECT.serializeObject(item);
  }

  sortOrder(item1: File, item2: File): number {
    return (item1.edited || item1.created) - (item2.edited || item2.created);
  }
}

export async function createFileGroup(fileGroup: FileGroup) {
  const filesTreeList = FilesTreeLoader.getListInstance(fileGroup.parentId);
  await filesTreeList.addListItem(fileGroup);
}

@JsonObject()
export class Notification extends BaseListItem {

  @JsonProperty()
  iconUrl: string;

  @JsonProperty()
  title: string;

  @JsonProperty()
  text: string;

  @JsonProperty()
  readAt: number;

  static createNew(profilePhoto: string, title: string, text: string): Notification {
    return new Notification(md5_uuid(), profilePhoto, title, text);
  }

  constructor(id: string, iconUrl: string, title: string, text: string) {
    super(id);
    this.iconUrl = iconUrl;
    this.title = title;
    this.text = text;
  }

  isUnread(): boolean {
    return this.readAt > 0;
  }
}

export class Notifications extends BaseListItemsLoader<Notification> {

  private static instance: Notifications;
  private static sharedInstance: Notifications;

  static getInstance() {
    if (!this.instance) {
      this.instance = new Notifications();
    }
    return this.instance;
  }

  static getSharedInstance() {
    if (!this.sharedInstance) {
      this.sharedInstance = new Notifications(true);
    }
    return this.sharedInstance;
  }

  private constructor(shared?: boolean) {
    super({shared: Boolean(shared)});
  }

  protected basePath(): string {
    return "notifications";
  }

  notifyAll(): void {
    this.observers.forEach(observer => observer.onItemChanged(null, ListItemChange.CHANGED));
  }

  //
  // async getOrLoadListItems(): Promise<Notification[]> {
  //   const fake = new Notification(md5_uuid());
  //   fake.title = "Hello, world";
  //   fake.text = "Lorem ipsum dolor sit amet";
  //   return [
  //     fake,
  //   ];
  // }

  protected deserializeItem(value: any): Notification {
    return JSON_OBJECT.deserializeObject(value, Notification);
  }

  protected serializeItem(item: Notification): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Notification, item2: Notification): number {
    return item2.created - item1.created;
  }
}

export enum BadgeSize {
  XSMALL = "xsm",
  SMALL = "sm",
  MEDIUM = "md",
  LARGE = "lg",
}

export type BadgeSizeMetadata = {
  size: number,
  fontSize: string,
}

export const BADGE_SIZE_KTM = new KeyMetadatas<BadgeSizeMetadata>([
  $KTM(BadgeSize.XSMALL, {
    size: 20,
    fontSize: "72%",
  }),
  $KTM(BadgeSize.SMALL, {
    size: 24,
    fontSize: "84%",
  }),
  $KTM(BadgeSize.MEDIUM, {
    size: 36,
    fontSize: "100%",
  }),
  $KTM(BadgeSize.LARGE, {
    size: 48,
    fontSize: "120%",
  }),
]);
